import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Phone, Database, UserCheck } from 'lucide-react';

const services = [
  {
    icon: Phone,
    title: 'AI Receptionist',
    description: 'Never miss a lead. AI answers calls 24/7, captures information, and recovers missed opportunities before they go to competitors.',
    outcomes: ['Missed calls recovered', 'After-hours coverage', '24/7 lead capture'],
  },
  {
    icon: Database,
    title: 'CRM + Pipeline Management',
    description: 'Every lead tracked, organized, and visible. Know exactly where each opportunity stands and never let follow-up slip.',
    outcomes: ['Full pipeline visibility', 'Lead status tracking', 'Automated reminders'],
  },
  {
    icon: UserCheck,
    title: 'Dedicated Appointment Setter',
    highlight: true,
    description: 'A real person assigned to your account who works the CRM, follows up with leads, revives cold opportunities, and helps turn more of your pipeline into booked jobs.',
    outcomes: ['Calls & texts your leads', 'Revives cold opportunities', 'Books more appointments', 'Pushes deals to close'],
  },
];

export const WhatWeDoSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  return (
    <section
      ref={sectionRef}
      id="services"
      className="py-20 relative"
      data-testid="what-we-do-section"
    >
      <div className="max-w-5xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            What We Do
          </h2>
          <p className="text-lg text-white/60 max-w-xl mx-auto">
            Three things that turn more of your leads into booked jobs and revenue.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
              className={`p-6 rounded-2xl border transition-all ${
                service.highlight 
                  ? 'bg-[#6C4DFF]/8 border-[#6C4DFF]/30' 
                  : 'bg-white/3 border-white/6 hover:border-white/12'
              }`}
              data-testid={`service-${index}`}
            >
              {service.highlight && (
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#6C4DFF]/20 text-[#6C4DFF] text-xs font-medium mb-4">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#6C4DFF]" />
                  Key Differentiator
                </div>
              )}
              
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                service.highlight ? 'bg-[#6C4DFF]/20' : 'bg-white/5'
              }`}>
                <service.icon className={`w-6 h-6 ${service.highlight ? 'text-[#6C4DFF]' : 'text-white/70'}`} />
              </div>

              <h3 className="text-xl font-semibold text-white mb-3">{service.title}</h3>
              <p className="text-sm text-white/60 mb-4 leading-relaxed">{service.description}</p>

              <div className="space-y-2">
                {service.outcomes.map((outcome, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className={`w-1 h-1 rounded-full ${service.highlight ? 'bg-[#6C4DFF]' : 'bg-[#32D583]'}`} />
                    <span className="text-sm text-white/50">{outcome}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
